import { Component, h } from 'preact'
import { render } from 'preact-render-to-string'
import Header from '../header'
import Panel from '../panel'
import Icon from '../assets'
import formatDateInLocalTimezone from '../utils/dateFormatting'
import InputGroup from '../ui-components/InputGroup'
import Axios, {AxiosResponse} from 'axios'
import { DropdownSelectizeInput } from '../dropdown/DropdownSelectize'
import Switcher from '../control/Switcher'
import Content from '../common/Content'
import ButtonControl from '../button-control'
import Editor from '../editor'
import UserAvatar from '../User/UserAvatar'
import globals from '../../utils/globals'
import {Topic, Alert, Reply, ModerationLabel, IdeaStatus} from './interfaces'
import FeaturedImageAttachment from '../category/control/Attachments'
import ProductAreaSelect, { ProductAreaType } from '../product-areas/ProductAreaSelect'
import { pascalToKebab } from '../utils/toKebabCase'
import TeamNotes from '../team-notes/TeamNotes'
import MergeIdeaModal from '../ideas/control/MergeIdeaModal'
import DropdownControl from "../dropdown/DropdownControl";
import AlertComponent from "../ui-components/AlertComponent";
import Post from './Post'
import PostEditor from '../editor/postEditorViaAPI'
import ListSelector from '../list-selector/ListSelector'
import UserList from '../list-selector/UserList'
import { User } from '../user-selector/User'
import classNames from 'classnames'
import Phrase, { fetchPhrase } from "../../libs/translationsUtils/phrase";
import {TranslationCategory} from "../../libs/translationsUtils/translations"

interface Props {
  topic: Topic,
  topicType: 'productUpdate' | 'idea',
}

interface State extends Topic {
  showEditor: boolean,
  isLoading: boolean | null,
  isMergeIdeaModalOpen: boolean,
  selectedProductAreas: Array<ProductAreaType>,
  allProductAreas: Array<ProductAreaType>,
  selectedModerationLabel: ModerationLabel | null,
  moderationLabels: Array<ModerationLabel>,
  ideaStatuses: Array<IdeaStatus>,
  alert: Alert | null
}

class TopicModeration extends Component<Props, State> {
  private readonly baseApiUrl: string;
  private readonly communityUrl: string;
  private content: string | null = null;
  private userId: string = window.inSidedData.user.userid;
  private ideaStatusChangedPhrase: { module: TranslationCategory, key: string } = {
    module: 'Common',
    key: 'reply.idea.status.changed'
  };

  constructor (props: Props) {
    super(props)
    this.state = {
      ...props.topic,
      votes: props.topic.votedBy?.length,
      likes: props.topic.likedBy?.length,
      selectedProductAreas: props.topic.productAreas || [],
      showEditor: false,
      isLoading: null,
      isMergeIdeaModalOpen: false,
      allProductAreas: [],
      selectedModerationLabel: props.topic.moderationLabel || null,
      ideaStatus: props.topic.ideaStatus || null,
      moderationLabels: [],
      ideaStatuses: [],
      alert: null
    }

    this.baseApiUrl = `/community/api/${props.topicType}s`
    this.communityUrl = `${globals('communityUrl')}/${pascalToKebab(props.topicType)}s/${this.props.topic.id}`

    this.onChangeTitle = this.onChangeTitle.bind(this)
    this.onChangeTag = this.onChangeTag.bind(this)
    this.onChangeModeratorTag = this.onChangeModeratorTag.bind(this)
    this.onChangeClosed = this.onChangeClosed.bind(this)
    this.onChangeOpeningPost = this.onChangeOpeningPost.bind(this)
    this.onChangeFeaturedImage = this.onChangeFeaturedImage.bind(this)
    this.onChangeProductAreas = this.onChangeProductAreas.bind(this)
    this.onChangeVote = this.onChangeVote.bind(this)
    this.onChangeLike = this.onChangeLike.bind(this)
    this.headerActions = this.headerActions.bind(this)
    this.onTrash = this.onTrash.bind(this)
    this.onAddReply = this.onAddReply.bind(this)
  }

  async componentDidMount () {
    await this.getReplies()
    await this.fetchPhrases([
        this.ideaStatusChangedPhrase
    ])

    if(this.isProductUpdate() || this.isIdea()) {
      const productAreasResponse = await Axios.get(`/community/api/productAreas`)
      if (productAreasResponse.data?.result) {
        this.setState({allProductAreas: productAreasResponse.data.result})
      }
    }

    if(this.isIdea()) {
      await Axios.get('/settings/label/getLabels')
          .then((response: AxiosResponse) => this.setState({ moderationLabels: response.data }))
    }
    await Axios.get(`${this.baseApiUrl}/ideaStatuses`)
        .then((response: AxiosResponse) => this.setState({ ideaStatuses: response.data.result }))
  }

  async onChangeTitle (newTitle: string) {
    this.setState({ isLoading: true })

    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editTitle`, {
      title: newTitle
    }).then(() => this.setState({ title: newTitle, isLoading: false }))
  }

  async onChangeProductAreas (newProductAreas: Array<ProductAreaType>) {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editProductAreas`, {
        productAreas: this.state.productAreas.map(selectedProductArea => selectedProductArea.id)
      }).then(() => this.setState({ productAreas: newProductAreas, isLoading: false }))
    } catch {
      this.setState(({ productAreas: oldProductAreas }) => ({ productAreas: oldProductAreas, isLoading: false }))
    }
  }

  async onChangeTag (newTags: Array<string>) {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editTags`, {
        tags: newTags
      }).then(() => this.setState({ tags: newTags, isLoading: false }))
    } catch {
      this.setState(({ tags: oldTags }) => ({ tags: oldTags, isLoading: false }))
    }
  }

  async onChangeModeratorTag (newTags: Array<string>) {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editModeratorTags`, {
        moderatorTags: newTags
      }).then(() => this.setState({ moderatorTags: newTags, isLoading: false }))
    } catch {
      this.setState(({ moderatorTags: oldModeratorTags }) => ({ moderatorTags: oldModeratorTags, isLoading: false }))
    }
  }

  async onChangeClosed (newValue: boolean) {
    this.setState({ isLoading: true })

    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/toggleClosed`, {
      closed: newValue
    }).then(() => this.setState({ closed: newValue, isLoading: false }))
  }

  async onChangeFeaturedImage (newFeaturedImage: null | { source_thumb: string }) {
    const newValue: string = newFeaturedImage === null ? '' : newFeaturedImage.source_thumb
    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editFeaturedImage`, {
      featuredImage: newValue
    }).then(() => this.setState({ featuredImage: newValue }))
  }

  async onChangeOpeningPost () {
    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/editContent`, {
      content: this.content
    }).then(() => this.setState({ showEditor: false, content: this.content }))
  }

  async onChangeVote () {
    if (this.state.votedBy?.includes(this.userId)) {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/unvote`, {
        unvotedBy: this.userId
      }).then(() => this.setState(({ votes, votedBy }) => {
        votedBy?.splice(votedBy?.findIndex(user => user === this.userId), 1)
        return { votes: (votes || 0) - 1, votedBy }
      }))
    } else {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/vote`, {
        votedBy: this.userId
      }).then(() => this.setState(({ votes, votedBy }) => ({ votes: (votes || 0) + 1, votedBy: votedBy && [...votedBy, this.userId] })))
    }
  }

  async onChangeLike () {
    if (this.state.likedBy?.includes(this.userId)) {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/unlike`, {
        unlikedBy: this.userId
      }).then(() => this.setState(({ likes, likedBy }) => {
        likedBy?.splice(likedBy?.findIndex(user => user === this.userId), 1)
        return { likes: (likes || 0) - 1, likedBy }
      }))
    } else {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/like`, {
        likedBy: this.userId
      }).then(() => this.setState(({ likes, likedBy }) => ({ likes: (likes || 0) + 1, likedBy: likedBy && [...likedBy, this.userId] })))
    }
  }

  async onTrash (item: string) {
    const setTrashed = async (trashed: boolean) => {
      await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/toggleTrashed`, {
        trashed
      }).then(() => this.setState({ trashed }))
    }

    if (item === 'Trash') {
      await setTrashed(true)
    } else if (item === 'Restore') {
      await setTrashed(false)
    }
  }

  async onAddReply () {
    await this.getReplies()
  }

  async getReplies () {
    const repliesResponse = await Axios.get(`${this.baseApiUrl}/${this.props.topic.id}/replies`)
    if (repliesResponse.data?.result) {
      this.setState({
        replies: repliesResponse.data.result,
        replyCount: repliesResponse.data.result.length
      })
    }
  }

  toggleMergeIdeaModal = () => {
    this.setState(({ isMergeIdeaModalOpen }) => ({ isMergeIdeaModalOpen: !isMergeIdeaModalOpen }))
  }

  onIdeaMergedSuccess = () => {
    this.setState({
      isMergeIdeaModalOpen: false,
      alert: {
        message: 'The idea has been merged successfully',
        type: 'success'
      }
    }, () => window.location.reload())
  }

  onModeratorAssigned = async (user: User) => {
    await Axios.post(`${this.baseApiUrl}/assignModerator`, {
      ideaIds: [this.props.topic.id],
      assignedTo: user.id
    }).then(() => this.setState({
      assignedModerator: user,
      alert: {
        type: 'success',
        message: `Assigned to ${user.name}!`
      }
    }))
    .catch(() => this.setState({
      alert: { type: 'error' }
    }))
  }

  onModeratorUnassigned = async () => {
    await Axios.post(`${this.baseApiUrl}/unassignModerator`, {
      ideaIds: [this.props.topic.id]
    }).then(() => this.setState({
      assignedModerator: undefined,
      alert: {
        type: 'success',
        message: 'Unassigned!'
      }
    }))
    .catch(() => this.setState({
      alert: { type: 'error' }
    }))
  }

  onModerationLabelSet = async (moderationLabel: ModerationLabel) => {
    this.setState({ isLoading: true })

    await Axios.post(`${this.baseApiUrl}/setModerationLabel`, {
      ideaIds: [this.props.topic.id],
      moderationLabelId: moderationLabel.id
    }).then(() => this.setState({ selectedModerationLabel: moderationLabel, isLoading: false }))
  }

  onModerationLabelUnset = async () => {
    this.setState({ isLoading: true })

    await Axios.post(`${this.baseApiUrl}/unsetModerationLabel`, {
      ideaIds: [this.props.topic.id],
    }).then(() => this.setState({ selectedModerationLabel: null, isLoading: false }))
  }

  onIdeaStatusChanged = async (ideaStatus: IdeaStatus) => {
    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/assignIdeaStatus`, {
      ideaStatusId: ideaStatus.id,
    })
    .then(async () => {
      const previousIdeaStatus = this.state.ideaStatus

      const previousStatusPill = this.buildPill(previousIdeaStatus)
      const newStatusPill = this.buildPill(ideaStatus)

      const phrase = render(<Phrase p={this.ideaStatusChangedPhrase.key} m={this.ideaStatusChangedPhrase.module} />)

      this.setState({
        ideaStatus,
        alert: {
          type: 'success',
          message: 'Ideation status has been updated!'
        }
      })

      const replyContent = `${phrase} ${previousStatusPill} → ${newStatusPill}`

      this.reply(replyContent)
          .then(() => this.getReplies())
          .catch(() => this.setState({
            alert: { type: 'error' }
          }))
    })
    .catch(() => this.setState({
      alert: { type: 'error' }
    }))
  }

  fetchPhrases = (phrasesSet: Array<{ module: TranslationCategory, key: string }>) => {
    return Promise.all(phrasesSet.map(async p => new Promise((resolve) => fetchPhrase(p.module, p.key, phrs => resolve(phrs)))))
  }

  buildPill = (ideaStatus: IdeaStatus) => {
    return `<span style="color:${ideaStatus.textColor};background-color:${ideaStatus.backgroundColor};" class="idea-status-pill">${ideaStatus.name}</span>`
  }

  reply = async (content: string) => {
    await Axios.post(`${this.baseApiUrl}/${this.props.topic.id}/reply`, {
      content,
    })
  }

  headerActions () {
    const { assignedModerator, ideaStatus, ideaStatuses } = this.state
    const value = this.state.trashed ? 'Restore' : 'Trash'

    return <div class='header-actions-wrapper'>
      { this.isIdea() && <DropdownControl
        align='adjust'
        triggerClass='dropdown-wrapper'
        trigger={
          <ButtonControl
              icon='arrowDropdown'
              title={`${ideaStatus ? ideaStatus.name : 'Select an idea status'}`}
              class='qa-topic-moderation-change-idea-status' />
        }
        items={ideaStatuses
            .filter((ideaStatus: IdeaStatus) => ideaStatus.visible)
            .filter((is: IdeaStatus) => is.id !== ideaStatus.id)
            .map((ideaStatus: IdeaStatus) => (
            {
              title: ideaStatus.name,
              value: ideaStatus.id,
              onClick: () => this.onIdeaStatusChanged(ideaStatus),
            }
        ))}
      /> }
      { this.isIdea() && <ButtonControl
        title='Merge idea'
        onClick={this.toggleMergeIdeaModal}
      /> }
      { this.isIdea() &&
        <DropdownControl
          align='adjust'
          triggerClass='dropdown-wrapper'
          trigger={
            <ButtonControl
              icon='arrowDropdown'
              title={`${assignedModerator ? assignedModerator.name : 'Assign moderator'}`}
              class='qa-topic-moderation-assign-moderator' />
          }
          items={[]}
        >
          {({ close } : { close: () => void}) => <ListSelector
            header={{
              title: assignedModerator ? 'Unassign' : 'Assign to me',
              onClick: () => assignedModerator ?
                  this.onModeratorUnassigned().then(() => close()) :
                  this.onModeratorAssigned({
                    id: window.inSidedData.user.userid,
                    name: window.inSidedData.user.name
                  }).then(() => close())
            }}
            isDynamic
            hasSearch
            fetchUrl='/member/getModerators?query='
            placeholder='Search for moderator'>
            {(users: User[]) => <UserList users={users} onClick={(user: User) => {
              this.onModeratorAssigned(user).then(() => close())
            }} />}
          </ListSelector> }
        </DropdownControl>
      }
      <DropdownControl
        align='adjust'
        triggerClass='dropdown-wrapper'
        trigger={
          <ButtonControl icon='arrowDropdown' title='More options' class='qa-topic-moderation-more-options' />
        }
        items={[{
          title: value,
          value,
          onClick: () => this.onTrash(value),
          variant: this.state.trashed ? 'primary' : 'danger'
        }]} />
    </div>
  }

  getCreatedDate = () => {
    let createdDate
    if (this.isIdea()) {
      createdDate = this.props.topic.submittedAt
    } else if (this.isProductUpdate()) {
      createdDate = this.props.topic.createdAt
    }

    return createdDate || new Date()
  }

  isProductUpdate = () => this.props.topicType === 'productUpdate'
  isIdea = () => this.props.topicType === 'idea'

  render({topic, topicType}: Props, {title, author, tags, moderatorTags, sticky, selectedProductAreas, closed, content, featuredImage, allProductAreas, moderationLabels, selectedModerationLabel, likes = 0, votes = 0, likedBy, votedBy, trashed, replyCount, showEditor, isLoading, isMergeIdeaModalOpen, alert, replies, ideaStatus, pinnedReplyId}: State) {
    const createTime = formatDateInLocalTimezone(new Date(this.getCreatedDate()).getTime() / 1000)
    return (
        <div>
          {alert &&
          <AlertComponent sticky isVisible message={alert.message} messageType={alert.type} isVisibleFor={3000}
                          onTimeoutEnd={() => this.setState({alert: null})}/>}
          <Header
              title='Moderate Topic'
              disableSaveButton
              disableCancelButton
              hasBreadcrumb
              breadcrumbLink='/moderation/overview'
              breadcrumbText='Back to Overview'
              customButtons={[this.headerActions()]}
          />
          <section id='topic-moderation--topic-info'>
            <div className='pill-container'>
              {trashed && <span className='pill pill--trashed'>trashed</span>}
              {ideaStatus && <span
                class='idea-status-pill'
                style={{
                  color: `#${ideaStatus.textColor}`,
                  backgroundColor: `#${ideaStatus.backgroundColor}`,
                }}>
            {ideaStatus.name}
          </span>}
            </div>
            <div className='topic-moderation--topic-info__title'>
              <h3>{title} <a href={this.communityUrl} target='_blank'><Icon icon='externalLink' width={16} height={16}/></a>
              </h3>
            </div>
            <ul className='topic-moderation--topic-info__metadata'>
              <li className='topic-moderation--topic-info__metadata-item'>{createTime}</li>
              <li className='topic-moderation--topic-info__metadata-item'>Votes: {votes}</li>
              <li className='topic-moderation--topic-info__metadata-item'>Replies: {replyCount}</li>
            </ul>
          </section>
          <Panel collapsible title='Details'>
            <div id='topic-moderation--thread-options'>
              <div className='panel-body' id='panelSettings'>
                <div className='topic-info__details-items'>
                  <div className='topic-info__details-item'>
                    <InputGroup
                        title='Title'
                        type='text'
                        onChange={(e: { target: HTMLInputElement }) => {
                          this.onChangeTitle(e.target.value)
                        }}
                        value={title}/>
                  </div>
                  <div className='topic-info__details-item'>
                    {
                      (this.isProductUpdate() || this.isIdea()) && allProductAreas.length ? (
                          <div className='form-group'>
                            <label>Product Areas</label>
                            <ProductAreaSelect
                                allProductAreas={allProductAreas}
                                selectedProductAreas={selectedProductAreas}
                                onUpdate={(newItems) => this.onChangeProductAreas(newItems)}
                            />
                          </div>
                      ) : null
                    }
                  </div>
                  <div className='topic-info__details-item'>
                    <label>Public tags</label>
                    <DropdownSelectizeInput
                        items={[]}
                        single={false}
                        class='form-control qa-topic-creator-public-tags-input'
                        placeholder='Public tags'
                        selectedItems={tags && tags.length ? tags : (tags || [])}
                        onChange={(tags: Array<string>) => this.onChangeTag(tags)}/>
                  </div>
                  <div className='topic-info__details-item'>
                    <label>Moderator tags</label>
                    <DropdownSelectizeInput
                        items={[]}
                        single={false}
                        class='form-control qa-topic-creator-public-tags-input'
                        placeholder='Moderator tags'
                        selectedItems={moderatorTags && moderatorTags.length ? moderatorTags : (moderatorTags || [])}
                        onChange={(tags) => this.onChangeModeratorTag(tags)}/>
                  </div>
                  <div className='topic-info__details-item'>
                    <label>Options</label>
                    <div>
                      <Switcher on={closed} onChange={() => this.onChangeClosed(!closed)} label='Close topic'/>
                    </div>
                  </div>
                  <div className='topic-info__details-item'/>
                  {this.isIdea() && <div className='topic-info__details-item full-width'>
                    <label>Labels</label>
                    <div class='moderation-label-container'>
                      {moderationLabels.map((moderationLabel: ModerationLabel) => {
                            const isSelected = moderationLabel.id === selectedModerationLabel?.id
                            return <ButtonControl
                                title={moderationLabel.title}
                                class={classNames('moderation-label', {'selected': isSelected})}
                                onClick={() => isSelected ? this.onModerationLabelUnset() : this.onModerationLabelSet(moderationLabel)}
                            />
                          }
                      )}
                    </div>
                  </div>}
                </div>
              </div>
              <div className='panel-footer clearfix'>
                {
                  isLoading === false ? ( // Should not be shown if isLoading is null
                      <div>All changes are saved.</div>
                  ) : null
                }
                <div className={`saving ${!isLoading ? 'hide' : ''}`}>
                  <div className='insided-loader'><span/></div>
                  <span>Saving changes</span>
                </div>
              </div>
            </div>
          </Panel>
          {this.isProductUpdate() &&
          <Panel collapsible title='Featured image' customClass='featured-image_panel'>
            <FeaturedImageAttachment
              label='Maximum file size: 5M. Allowed image types: .jpg, .jpeg, .png, .gif. We recommend a minimum of 1440*640px'
              className='featured-image'
              isAttachmentSelected={featuredImage}
              imageSrc={featuredImage}
              width={1440}
              height={640}
              onUpdate={(data: null | { source_thumb: string }) => this.onChangeFeaturedImage(data)}
            />
          </Panel>
          }
          <TeamNotes topicContentType={topicType} topicId={topic.publicId} topicTitle={topic.title}
                     topicUrl={window.location.href}/>
          <section id='topic-moderation--posts-container'>
            <h4><strong>Opening post</strong></h4>
            <div className='topic-moderation--posts-first'>
              <article className='post panel panel-default  status--author'>
                <div className='post--inner-wrapper'>
                  <div className='panel-body'>
                    <div className='user-avatar--container clearfix'>
                      <UserAvatar
                          user={{...author, ...{name: author.username}, ...{url: `/member/profile/?userid=${author.id}`}}}
                          extraClass='avatar avatar--L'
                          style={{float: 'left'}}
                          showRank
                          openInNewTab
                      />
                      <div className='user-avatar--info'>
                        <h4>
                          <strong>
                            <a href={`/member/profile/?userid=${author.id}`}>{author.username}</a>
                          </strong>
                        </h4>
                        <span className='text-muted posts-count clearfix'>{author.reputation.repliesMade} replies</span>
                      </div>
                    </div>
                    <div className='post--content-container'>
                      <header className='clearfix'>
                      <span className='text-muted post--time'>
                        Posted on {createTime}
                      </span>
                        <div className='pull-right pill-container'>
                          <span className='pill pill--author'>author</span>
                        </div>
                      </header>
                      <div className='post--content post--content-new-editor'>
                        {
                          showEditor ? (
                              <Editor
                                  isControl
                                  data={this.state.content}
                                  attachments={[]}
                                  canHavePoll={false}
                                  onSubmit={this.onChangeOpeningPost}
                                  onCancel={() => this.setState({showEditor: false})}
                                  onChange={(content: string) => {
                                    this.content = content
                                  }}/>
                          ) : (
                              <div>
                                <Content
                                    content={content || ''}
                                    className='qa-post-content'
                                    isControl
                                />
                                { this.isIdea() &&
                                replies &&
                                pinnedReplyId &&
                                <PinnedReplyInContent
                                  pinnedReply={replies.filter((reply: Reply) => reply.id === pinnedReplyId)[0]}
                                /> }
                              </div>
                          )
                        }
                      </div>
                      {this.isIdea() && <footer className='post--options-list'>
                        <a className={votedBy?.includes(this.userId) ? 'active' : ''} onClick={this.onChangeVote}>
                          <i className='fa fa-thumbs-up'/> Vote ({votes})
                        </a>
                      </footer>}
                      {this.isProductUpdate() && <footer className='post--options-list'>
                        <a className={likedBy?.includes(this.userId) ? 'active' : ''} onClick={this.onChangeLike}>
                          <i className='fa fa-thumbs-up'/> Like ({likes})
                        </a>
                      </footer>}
                    </div>
                  </div>
                  <div className='post__panel-footer clearfix'>
                    <div/>
                    <div className='post__panel-footer--action-box js-post-panel-footer-action-box'>
                      <ButtonControl
                          title='Edit post'
                          variant='secondary'
                          onClick={() => this.setState({showEditor: true})}
                      />
                    </div>
                  </div>
                </div>
              </article>
            </div>
            <h4><strong>{replyCount} Replies</strong></h4>
            {replies && replies.map((reply: Reply, index: number) =>
                <Post {...reply}
                      isAuthor={reply.author.id === author.id}
                      isPinned={pinnedReplyId === reply.id}
                      replyNumber={index + 1}
                      replyCount={replyCount}
                      topicType={topicType}
                      topicId={topic.id}
                      topicPublicId={topic.publicId}
                      isIdea={this.isIdea()}
                      onTogglePin={(pinnedReplyId: string | null) => this.setState({pinnedReplyId})}
                />
            )}
          </section>
          <section id="topic-moderation--editor" className="topic-moderation--wysiwyg-editor">
            <h4><strong>Write a reply</strong></h4>
            <PostEditor
                replyId={null}
                topicId={topic.id}
                isControl
                onSuccess={this.onAddReply}
                type={`${topicType}s` as 'productUpdates' | 'ideas'}
                redirect={false}
                isGuest={false}
                disableFileAttachments={false}
            />
            <br/>
          </section>
          {this.isIdea() && isMergeIdeaModalOpen &&
          <MergeIdeaModal fromIdea={this.props.topic}
                          fromIdeaStatus={this.state.ideaStatus}
                          baseApiUrl={this.baseApiUrl}
                          isOpen={isMergeIdeaModalOpen}
                          onClose={this.toggleMergeIdeaModal}
                          onIdeaMergedSuccess={this.onIdeaMergedSuccess}
                          ideaStatuses={this.state.ideaStatuses}
                          fetchPhrases={this.fetchPhrases}
                          buildPill={this.buildPill}
          />}
        </div>
    )
  }
}

const PinnedReplyInContent = ({ pinnedReply } : { pinnedReply : Reply }) => {
  const { id, author, content, repliedAt } = pinnedReply

  return <Content
      content={
        `<div className="post--answer--container">
            <strong>Pinned reply by ${author.username} - ${formatDateInLocalTimezone(new Date(repliedAt).getTime() / 1000 | 0)}</strong>
            <div>${content}</div>
            <br />
            <a href='#post-${id}'>View original</a>
          </div>`
      }
      className='post--answer--container'
      isControl
  />
}


export default TopicModeration
